/**
 * CapacityTeamTotalRow.tsx
 * The per-week "team total" footer row of the merged "Team Allocation & Weekly
 * Capacity" grid (staffing-allocation follow-up 2026-07-13) — the summed
 * allocation % THIS plan places on each week's roster. Extracted from
 * Phase2CapacityTable to keep it under the 600-line cap.
 */

import React from 'react';
import { TableCell, TableRow, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import { EMP_COL_W, ROLE_COL_W, WEEK_W } from './phase2CapacityShared';

export interface CapacityTeamTotalRowProps {
  weekColumns: string[];
  /** weekStart → Σ this-plan allocation % for that week. */
  teamTotalByWeek: Map<string, number>;
  hoveredWeek?: string | null;
  onHoverWeek?: (weekStart: string | null) => void;
}

export const CapacityTeamTotalRow: React.FC<CapacityTeamTotalRowProps> = ({
  weekColumns,
  teamTotalByWeek,
  hoveredWeek,
  onHoverWeek,
}) => {
  const theme = useTheme();
  const beamRail = `2px solid ${theme.schedule.selection}`;

  return (
    <TableRow>
      <TableCell
        colSpan={2}
        sx={{
          position: 'sticky',
          left: 0,
          zIndex: 1,
          backgroundColor: theme.table.headerBg,
          boxShadow: `inset -1px 0 0 0 ${theme.border.color}`,
          borderTop: `2px solid ${theme.border.color}`,
          py: 0.5,
          px: 1.5,
          width: EMP_COL_W + ROLE_COL_W,
        }}
      >
        <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
          Team total (this plan)
        </Typography>
      </TableCell>
      {weekColumns.map((ws) => {
        const total = Math.round(teamTotalByWeek.get(ws) ?? 0);
        const hot = hoveredWeek === ws;
        return (
          <TableCell
            key={ws}
            colSpan={7}
            align="center"
            onMouseEnter={() => onHoverWeek?.(ws)}
            sx={{
              width: WEEK_W,
              py: 0.5,
              borderTop: `2px solid ${theme.border.color}`,
              backgroundColor: hot ? alpha(theme.schedule.selection, 0.06) : theme.table.headerBg,
              borderRight: hot ? beamRail : `2px solid ${theme.border.color}`,
              ...(hot ? { borderLeft: beamRail } : {}),
            }}
          >
            <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>
              {total}%
            </Typography>
          </TableCell>
        );
      })}
    </TableRow>
  );
};
